import { UIButton, UIColor, UIComponentView, UIView } from "uicore-ts"
import { BSColor } from "./BSColor"

export type RHButtonVariant =
    | "primary"
    | "secondary"
    | "success"
    | "danger"
    | "warning"
    | "info"
    | "light"
    | "dark"
    | "link"

@UIComponentView
export class RHButton extends UIButton {
    
    // Configurable constant for sheen effect intensity (0.0 to 0.5)
    static sheenIntensity: number = 0.15
    
    // Define transitions to reuse logic
    // We animate box-shadow and margin-top (the lift).
    // We do NOT animate transform, as it is used by framework for layout positioning.
    // We do NOT animate background properties to keep color changes snappy/responsive.
    private static liftTransition = "box-shadow .15s ease-in-out, margin-top .15s ease-in-out"
    private static instantPressTransition = "box-shadow .15s ease-in-out" // Removed margin-top for instant drop
    
    _variant: RHButtonVariant = "primary"
    _rippleView: UIView = new UIView().configuredWithObject({
        alpha: 0,
        style: {
            position: "absolute",
            borderRadius: "50%",
            backgroundColor: "rgba(255,255,255, 0.3)",
            pointerEvents: "none",
            transform: "translate(-50%, -50%) scale(0)",
            transition: "transform 0.4s ease-out, opacity 0.4s ease-out",
            top: "50%",
            left: "50%",
            width: "250%",
            paddingBottom: "250%" // Aspect ratio hack
        }
    }).addedAsSubviewToView(this)
    
    
    constructor(elementID?: string) {
        
        // Pass "button" to super to ensure correct HTML tag
        super(elementID, "button")
        
        
        // Use configureWithObject for deep style assignment
        // @ts-ignore
        this.configureWithObject({
            style: {
                display: "inline-block",
                fontWeight: "400",
                lineHeight: "1.5",
                textAlign: "center",
                textDecoration: "none",
                verticalAlign: "middle",
                cursor: "pointer",
                userSelect: "none",
                border: "1px solid transparent",
                padding: "0.375rem 0.75rem",
                fontSize: "1rem",
                borderRadius: "6px",
                // Start with no transition
                transition: "",
                boxSizing: "border-box",
                overflow: "hidden" // Clips ripple
            }
        })
        
        // FIX: Configure.titleLabel to prevent visual jumping ("glitch") and ensure stable centering
        if (this.titleLabel) {
            this.titleLabel.configureWithObject({
                style: {
                    zIndex: "10",
                    lineHeight: "1.2", // Explicit line-height prevents layout shifts on hover
                    willChange: "transform" // Hint for GPU acceleration
                }
            })
        }
        
        // Apply default variant
        this.setVariant("primary")
        
    }
    
    
    // // FILTER HOVER EVENTS TO PREVENT FLICKER
    // override async shouldCallPointerHover(event: MouseEvent | TouchEvent) {
    //     const superResult = super.shouldCallPointerHover(event)
    //     if (!superResult) {
    //         return NO
    //     }
    //
    //     // Only apply logic to Mouse events on the bottom edge
    //     if (event instanceof MouseEvent) {
    //         const rect = this.viewHTMLElement.getBoundingClientRect()
    //         const mouseY = event.clientY - rect.top
    //         const height = rect.height
    //         const buffer = 1 // 1px dead zone at bottom
    //
    //         // If mouse is within the bottom buffer zone, ignore the hover.
    //         // This prevents the loop: Lift -> Mouse visually outside -> Drop -> Enter again.
    //         if (mouseY > height - buffer) {
    //             return NO
    //         }
    //     }
    //
    //     return YES
    // }
    
    get variant(): RHButtonVariant {
        return this._variant
    }
    
    set variant(value: RHButtonVariant) {
        this.setVariant(value)
    }
    
    setVariant(variant: RHButtonVariant) {
        this._variant = variant
        
        // Use configureWithObject to batch update color specifier
        const colorConfig = this.getColorConfigForVariant(variant)
        
        // @ts-ignore
        this.configureWithObject({
            colors: colorConfig
        })
        
        this.updateContentForCurrentState()
    }
    
    // Helper to map BSColor to UIButtonColorSpecifier structure
    private getColorConfigForVariant(variant: RHButtonVariant) {
        let textColor = BSColor.textWhite
        let bgNormal, bgHover, bgActive, bgFocus
        
        // Logic to select correct BSColor sets
        switch (variant) {
            case "secondary":
                bgNormal = BSColor.secondary
                bgHover = BSColor.secondaryHover
                bgActive = BSColor.secondaryActive
                bgFocus = BSColor.secondaryHover
                break
            case "success":
                bgNormal = BSColor.success
                bgHover = BSColor.successHover
                bgActive = BSColor.successActive
                bgFocus = BSColor.successHover
                break
            case "danger":
                bgNormal = BSColor.danger
                bgHover = BSColor.dangerHover
                bgActive = BSColor.dangerActive
                bgFocus = BSColor.dangerHover
                break
            case "warning":
                textColor = BSColor.textBlack
                bgNormal = BSColor.warning
                bgHover = BSColor.warningHover
                bgActive = BSColor.warningActive
                bgFocus = BSColor.warningHover
                break
            case "info":
                textColor = BSColor.textBlack
                bgNormal = BSColor.info
                bgHover = BSColor.infoHover
                bgActive = BSColor.infoActive
                bgFocus = BSColor.infoHover
                break
            case "light":
                textColor = BSColor.textBlack
                bgNormal = BSColor.light
                bgHover = BSColor.lightHover
                bgActive = BSColor.lightActive
                bgFocus = BSColor.lightHover
                break
            case "dark":
                bgNormal = BSColor.dark
                bgHover = BSColor.darkHover
                bgActive = BSColor.darkActive
                bgFocus = BSColor.darkHover
                break
            case "link":
                textColor = BSColor.primary
                bgNormal = UIColor.transparentColor
                bgHover = UIColor.transparentColor
                bgActive = UIColor.transparentColor
                bgFocus = UIColor.transparentColor
                break
            case "primary":
            default:
                bgNormal = BSColor.primary
                bgHover = BSColor.primaryHover
                bgActive = BSColor.primaryActive
                bgFocus = BSColor.primaryHover
                break
        }
        
        return {
            titleLabel: {
                normal: textColor,
                hovered: textColor,
                highlighted: textColor,
                selected: textColor
            },
            background: {
                normal: bgNormal,
                hovered: bgHover,
                highlighted: bgActive,
                focused: bgFocus
            }
        }
    }
    
    // Helper to generate gradient style dynamically for any color
    private getGradientStyles(baseColor: UIColor, isInset: boolean = false, isHovered: boolean = false) {
        
        // Do not apply gradient/shadow to Link variant
        if (this._variant === "link") {
            return {
                backgroundImage: "none",
                boxShadow: "none"
            }
        }
        
        // Create lighter color (Highlight) and darker color (Shadow)
        const lighterColor = baseColor.colorByMultiplyingRGB(1 + RHButton.sheenIntensity)
        const darkerColor = baseColor.colorByMultiplyingRGB(1 - RHButton.sheenIntensity)
        
        // Gradient: Light (Top Left) -> Base (Middle) -> Dark (Bottom Right)
        const gradientString = `linear-gradient(165deg, ${lighterColor.stringValue} 0%, ${baseColor.stringValue} 50%, ${darkerColor.stringValue} 100%)`
        
        // Use shadow color defined for variant
        const shadowColor = this.getShadowColorForVariant(this._variant)
        let shadowString: string
        
        if (isInset) {
            // Pressed state
            shadowString = "inset 0 3px 5px rgba(0, 0, 0, 0.125)"
        }
        else {
            // Drop shadow logic
            const shadowOpacity = 0.2
            
            if (isHovered) {
                // Increased shadow for hover (lifted effect)
                shadowString = `0 5px 15px 2px ${shadowColor.colorWithAlpha(shadowOpacity).stringValue}`
            }
            else {
                // Normal shadow
                shadowString = `0 2px 6px 0px ${shadowColor.colorWithAlpha(shadowOpacity).stringValue}`
            }
        }
        
        return {
            backgroundImage: gradientString,
            boxShadow: shadowString
        }
    }
    
    override updateContentForNormalState() {
        super.updateContentForNormalState()
        
        const config = this.getColorConfigForVariant(this._variant)
        const styles = this.getGradientStyles(config.background.normal, false, false)
        
        // @ts-ignore
        this.configureWithObject({
            style: {
                ...styles,
                borderColor: this.getBorderColorForState("normal").stringValue
            }
        })
        
        // RESTORE lift transition (allow smooth lifts on next hover)
        this.style.transition = RHButton.liftTransition
        // Reset lift
        this.style.marginTop = ""
        
        // Reset Lift (Frame Transform) - not used anymore but good hygiene
        this.frameTransform = ""
        
        // Reset Label Transform to center
        if (this.titleLabel) {
            this.titleLabel.style.transform = "translateY(-50%)"
        }
        
        this._rippleView.style.transform = "translate(-50%, -50%) scale(0)"
        this._rippleView.alpha = 0
    }
    
    override updateContentForHoveredState() {
        super.updateContentForHoveredState()
        
        const config = this.getColorConfigForVariant(this._variant)
        const styles = this.getGradientStyles(config.background.hovered, false, true) // true = hover shadow
        
        this.style.borderColor = this.getBorderColorForState("hover").stringValue
        
        // Apply new gradient and shadow for hover state
        // @ts-ignore
        this.configureWithObject({
            style: styles
        })
        
        // RESTORE lift transition (includes margin-top)
        this.style.transition = RHButton.liftTransition
        
        // Lift Button (Using margin-top to avoid animating framework's transform)
        this.style.marginTop = "-1px"
        
        // Ensure Label stays centered
        if (this.titleLabel) {
            this.titleLabel.style.transform = "translateY(-50%)"
        }
        
        // Ripple Effect from center
        this._rippleView.alpha = 1
        this._rippleView.style.transform = "translate(-50%, -50%) scale(1)"
    }
    
    override updateContentForHighlightedState() {
        super.updateContentForHighlightedState()
        
        const config = this.getColorConfigForVariant(this._variant)
        const styles = this.getGradientStyles(config.background.highlighted, true, false) // true = inset shadow
        
        // @ts-ignore
        this.configureWithObject({
            style: {
                ...styles,
                borderColor: this.getBorderColorForState("active").stringValue
            }
        })
        
        // REMOVE margin-top from transition to make movement INSTANT
        this.style.transition = RHButton.instantPressTransition
        
        // Reset lift (Instant snap down)
        this.style.marginTop = ""
        
        // Move Label Down to indicate press
        if (this.titleLabel) {
            // Instant transform change
            this.titleLabel.style.transition = ""
            this.titleLabel.style.transform = "translateY(calc(-50% + 1px))"
        }
    }
    
    override updateContentForSelectedState() {
        
        super.updateContentForSelectedState()
        
        const config = this.getColorConfigForVariant(this._variant)
        const styles = this.getGradientStyles(config.background.highlighted, true, false) // true = inset shadow
        
        // @ts-ignore
        this.configureWithObject({
            style: {
                ...styles,
                borderColor: this.getBorderColorForState("active").stringValue
            }
        })
        
        // REMOVE margin-top from transition to make movement INSTANT
        this.style.transition = RHButton.instantPressTransition
        
        // Reset lift (Instant snap down)
        this.style.marginTop = ""
        
        // Move Label Down to indicate press
        if (this.titleLabel) {
            // Instant transform change
            this.titleLabel.style.transition = ""
            this.titleLabel.style.transform = "translateY(calc(-50% + 1px))"
        }
    }
    
    override updateContentForFocusedState() {
        super.updateContentForFocusedState()
        
        const shadowColor = this.getShadowColorForVariant(this._variant)
        const config = this.getColorConfigForVariant(this._variant)
        
        // Calculate gradient specifically for focused state (usually normal color)
        const baseColor = config.background.normal
        const lighterColor = baseColor.colorByMultiplyingRGB(1 + RHButton.sheenIntensity)
        const darkerColor = baseColor.colorByMultiplyingRGB(1 - RHButton.sheenIntensity)
        
        // @ts-ignore
        this.configureWithObject({
            style: {
                backgroundImage: `linear-gradient(165deg, ${lighterColor.stringValue} 0%, ${baseColor.stringValue} 50%, ${darkerColor.stringValue} 100%)`,
                borderColor: this.getBorderColorForState("hover").stringValue,
                boxShadow: `0 0 0 0.25rem ${shadowColor.stringValue}` // Focus ring overrides shadow
            }
        })
        
        // RESTORE lift transition
        this.style.transition = RHButton.liftTransition
        
        // Lift button
        this.style.marginTop = "-1px"
        
        // Ensure Label stays centered
        if (this.titleLabel) {
            this.titleLabel.style.transform = "translateY(-50%)"
        }
        
        if (this.hovered) {
            this._rippleView.alpha = 1
            this._rippleView.style.transform = "translate(-50%, -50%) scale(1)"
        }
    }
    
    private getBorderColorForState(state: "normal" | "hover" | "active"): UIColor {
        switch (this._variant) {
            case "secondary":
                switch (state) {
                    case "hover":
                        return BSColor.secondaryBorderHover
                    case "normal":
                        return BSColor.secondaryBorder
                    default:
                        return BSColor.secondaryBorderActive
                }
            case "success":
                switch (state) {
                    case "hover":
                        return BSColor.successBorderHover
                    case "normal":
                        return BSColor.successBorder
                    default:
                        return BSColor.successBorderActive
                }
            case "danger":
                switch (state) {
                    case "normal":
                        return BSColor.dangerBorder
                    case "hover":
                        return BSColor.dangerBorderHover
                    default:
                        return BSColor.dangerBorderActive
                }
            case "warning":
                switch (state) {
                    case "normal":
                        return BSColor.warningBorder
                    case "hover":
                        return BSColor.warningBorderHover
                    default:
                        return BSColor.warningBorderActive
                }
            case "info":
                switch (state) {
                    case "normal":
                        return BSColor.infoBorder
                    case "hover":
                        return BSColor.infoBorderHover
                    default:
                        return BSColor.infoBorderActive
                }
            case "light":
                switch (state) {
                    case "normal":
                        return BSColor.lightBorder
                    case "hover":
                        return BSColor.lightBorderHover
                    default:
                        return BSColor.lightBorderActive
                }
            case "dark":
                switch (state) {
                    case "normal":
                        return BSColor.darkBorder
                    case "hover":
                        return BSColor.darkBorderHover
                    default:
                        return BSColor.darkBorderActive
                }
            case "link":
                return BSColor.transparentColor
            default:
                switch (state) {
                    case "normal":
                        return BSColor.primaryBorder
                    case "hover":
                        return BSColor.primaryBorderHover
                    default:
                        return BSColor.primaryBorderActive
                }
        }
    }
    
    private getShadowColorForVariant(variant: RHButtonVariant) {
        const map = {
            secondary: BSColor.secondaryShadow,
            success: BSColor.successShadow,
            danger: BSColor.dangerShadow,
            warning: BSColor.warningShadow,
            info: BSColor.infoShadow,
            light: BSColor.lightShadow,
            dark: BSColor.darkShadow,
            link: BSColor.colorWithRGBA(13, 110, 253, 0.25),
            primary: BSColor.primaryShadow
        }
        const result = map[variant] ?? BSColor.primaryShadow
        return result
    }
}
